/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/field_change_suggestions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-field_change_suggestions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/field_change_suggestions-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, target_record_id, suggested_by_user_profile_id, status, reviewed_by_user_profile_id
 * - Sortable fields: updated_at
 * - Required fields: id, target_table, target_record_id, field_name, suggested_value, suggested_by_user_profile_id, suggested_at, status, is_junction, updated_at
 * - Readonly fields: id, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/field_change_suggestions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-field_change_suggestions-api-client.ts (API client)
 * - client/api/generated_schemas/field_change_suggestions.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/field_change_suggestions-entity-config.ts
 * Public fields: 15/15 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FieldChangeSuggestionsClient, FieldChangeSuggestionsQueryOptions } from '../generated_clients/generated-field_change_suggestions-api-client.js';
import {
  FieldChangeSuggestionsInsert,
  FieldChangeSuggestionsUpdate,
  FieldChangeSuggestionsFieldConfig
} from '../../types/generated_schemas/generated-field_change_suggestions-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/field_change_suggestions_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const fieldChangeSuggestionsKeys = {
  all: ['field_change_suggestions'] as const,
  lists: () => [...fieldChangeSuggestionsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<FieldChangeSuggestionsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...fieldChangeSuggestionsKeys.lists(), { filters, ...page }] as const,
  details: () => [...fieldChangeSuggestionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...fieldChangeSuggestionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...fieldChangeSuggestionsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...fieldChangeSuggestionsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...fieldChangeSuggestionsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...fieldChangeSuggestionsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useFieldChangeSuggestionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new FieldChangeSuggestionsClient();
};

/**
 * Hook to get all field_change_suggestions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with field_change_suggestions data
 */
export const useFieldChangeSuggestionsList = (options?: FieldChangeSuggestionsQueryOptions) => {
  const client = useFieldChangeSuggestionsClient();
  
  return useQuery({
    queryKey: fieldChangeSuggestionsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single field_change_suggestion by ID
 *
 * @param id - The field_change_suggestion ID
 * @returns React Query result with single field_change_suggestion data
 */
export const useFieldChangeSuggestions = (id: string) => {
  const client = useFieldChangeSuggestionsClient();
  
  return useQuery({
    queryKey: fieldChangeSuggestionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/field_change_suggestions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/field_change_suggestions_business_hooks/
 */

/**
 * Hook to create a new field_change_suggestion
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, target_table, target_record_id, field_name, suggested_value, suggested_by_user_profile_id, suggested_at, status, is_junction, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, updated_at
 *
 * @returns React Query mutation for creating field_change_suggestion
 */
export const useCreateFieldChangeSuggestions = () => {
  const client = useFieldChangeSuggestionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: FieldChangeSuggestionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fieldChangeSuggestionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing field_change_suggestion
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, updated_at
 *
 * @returns React Query mutation for updating field_change_suggestion
 */
export const useUpdateFieldChangeSuggestions = () => {
  const client = useFieldChangeSuggestionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FieldChangeSuggestionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: fieldChangeSuggestionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: fieldChangeSuggestionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a field_change_suggestion
 *
 * @returns React Query mutation for deleting field_change_suggestion
 */
export const useDeleteFieldChangeSuggestions = () => {
  const client = useFieldChangeSuggestionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fieldChangeSuggestionsKeys.all });
    },
  });
};

/**
 * Hook to count field_change_suggestions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, target_record_id, suggested_by_user_profile_id, status, reviewed_by_user_profile_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useFieldChangeSuggestionsCount = (filter?: Partial<Record<typeof FieldChangeSuggestionsFieldConfig.filterable[number], any>>) => {
  const client = useFieldChangeSuggestionsClient();
  
  return useQuery({
    queryKey: fieldChangeSuggestionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useFieldChangeSuggestionsFieldConfig = () => {
  return FieldChangeSuggestionsFieldConfig;
};